'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Building2, User, ArrowRight, Sparkles, CheckCircle2, Scale } from 'lucide-react';
import { Card } from '@/components/ui/Card';

export default function RegisterPage() {
  const router = useRouter();
  const [hovered, setHovered] = useState<'organization' | 'individual' | 'individual-organizer' | 'jury' | null>(null);

  const options = [
    {
      id: 'organization',
      title: 'Organization',
      description: 'Educational institutions, corporate partners, and festival hosts.',
      features: ['Host global events', 'Manage team accounts', 'Analytics dashboard'],
      icon: <Building2 className="w-8 h-8" />,
      path: '/register/organization',
      gradient: 'from-emerald-500/10 to-eucalyptus/10',
      iconBg: 'bg-emerald-500/10 text-emerald-600',
      borderHover: 'hover:border-emerald-500/30'
    },
    {
      id: 'individual',
      title: 'Participant',
      description: 'Event participants and solo attendees who join experiences.',
      features: ['Participate in events', 'Build your profile', 'Track registrations'],
      icon: <User className="w-8 h-8" />,
      path: '/register/individual',
      gradient: 'from-mist/30 to-eucalyptus/10',
      iconBg: 'bg-moss/10 text-soot',
      borderHover: 'hover:border-moss/30'
    },
    {
      id: 'jury',
      title: 'Jury',
      description: 'Experts, judges, and evaluators for event submissions.',
      features: ['Apply as jury member', 'Showcase expertise', 'Review event submissions'],
      icon: <Scale className="w-8 h-8" />,
      path: '/register/individual?mode=jury',
      gradient: 'from-amber-500/10 to-orange-500/10',
      iconBg: 'bg-amber-500/10 text-amber-600',
      borderHover: 'hover:border-amber-500/30'
    },
    {
      id: 'individual-organizer',
      title: 'Individual Organizer',
      description: 'Solo hosts and creators who want to run and manage events.',
      features: ['Create and host events', 'Manage attendees', 'Track event performance'],
      icon: <Building2 className="w-8 h-8" />,
      path: '/register/individual?mode=organizer',
      gradient: 'from-mist/30 to-eucalyptus/10',
      iconBg: 'bg-moss/10 text-soot',
      borderHover: 'hover:border-moss/30'
    }
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-landing-bg via-white to-landing-primary/5 flex items-center justify-center p-6 sm:p-12">
      <div className="max-w-7xl w-full">
        <div className="text-center mb-12 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-landing-primary/10 text-landing-primary text-[10px] font-bold uppercase tracking-widest mb-4">
            <Sparkles className="w-3 h-3" />
            Get Started with EnthiosHub
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-nature-text tracking-tight h-[1.2em]">
            Choose your <span className="text-landing-primary">path</span>
          </h1>
          <p className="mt-3 text-base text-nature-muted max-w-xl mx-auto font-medium">
            Join the world's most interactive event platform. Select the type of account that matches your needs.
          </p>
        </div>

        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-7 max-w-7xl mx-auto">
          {options.map((opt, idx) => (
            <div
              key={opt.id}
              className={`group relative animate-in fade-in slide-in-from-bottom-8 duration-700 fill-mode-both`}
              style={{ animationDelay: `${idx * 150}ms` }}
              onMouseEnter={() => setHovered(opt.id as any)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => router.push(opt.path)}
            >
              <Card 
                className={`h-full cursor-pointer overflow-hidden border-2 border-landing-border bg-bg/80 backdrop-blur-xl transition-all duration-500 ${opt.borderHover} ${hovered === opt.id ? 'shadow-[0_20px_50px_-12px_rgba(45,53,54,0.12)] -translate-y-1.5' : 'shadow-none'}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${opt.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="relative p-5 flex flex-col h-full">
                  <div className={`w-10 h-10 ${opt.iconBg} rounded-[14px] flex items-center justify-center mb-3 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-sm`}>
                    <div className="scale-90 origin-center">{opt.icon}</div>
                  </div>

                  <h3 className="text-xl font-bold text-nature-text mb-1.5 tracking-tight">{opt.title}</h3>
                  <p className="text-nature-muted text-xs font-medium leading-relaxed mb-3">{opt.description}</p>

                  <div className="space-y-2 mb-4 overflow-hidden">
                    {opt.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-nature-text text-xs font-semibold leading-snug">
                        <CheckCircle2 className={`w-3.5 h-3.5 ${opt.iconBg.split(' ')[1]} opacity-80`} />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-auto flex items-center gap-1.5 text-landing-primary text-xs font-bold transition-all duration-300">
                    <span>Register as {opt.title}</span>
                    <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${hovered === opt.id ? 'translate-x-1.5' : ''}`} />
                  </div>
                </div>
              </Card>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center animate-in fade-in duration-1000 delay-500 fill-mode-both">
          <p className="text-nature-muted font-medium">
            Already have an account?{' '}
            <Link href="/login" className="text-landing-primary font-bold hover:underline">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}


